import React from "react";
import {
  Box,
  Text,
  SimpleGrid,
  Flex,
  Icon,
  Badge,
  List,
  ListItem,
  ListIcon,
  CircularProgress,
  CircularProgressLabel,
  useColorModeValue,
  VStack,
  HStack,
} from "@chakra-ui/react";
import { FiImage, FiAlertTriangle, FiEyeOff, FiTrendingUp } from "react-icons/fi";
import { FaFire } from "react-icons/fa";
import { useTranslation } from "react-i18next";

export interface MonkeyRoastResult {
  imageAnalysis: string;
  potentialProblems: string[];
  whatImagesDontUnlock: string[];
  whatImagesDo: string[];
  score: number;
  updatedAt: string;
}

interface MonkeyRoastReportProps {
  roast: MonkeyRoastResult | null;
  isLoading: boolean;
}

const MonkeyRoastReport: React.FC<MonkeyRoastReportProps> = ({ roast, isLoading }) => {
  const { t } = useTranslation();
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  if (isLoading) {
    return (
      <Box mt={8} textAlign="center" py={10}>
        <CircularProgress isIndeterminate color="orange.400" />
        <Text mt={4} color="gray.500">{t("monkeyRoastInProgress")}</Text>
      </Box>
    );
  }

  if (!roast) return null;

  const scoreColor = roast.score >= 75 ? "green.400" : roast.score >= 50 ? "orange.400" : "red.400";

  return (
    <Box mt={10} as="section">
      <Flex align="center" mb={6} gap={3} flexWrap="wrap">
        <Text fontSize="2xl" fontWeight="bold" bgGradient="linear(to-r, orange.400, red.500)" bgClip="text">
          {t("monkeyRoastReport")}
        </Text>
        <Badge colorScheme="orange" variant="subtle" fontSize="0.9em" borderRadius="full" px={3}>
          <HStack spacing={1}>
            <Icon as={FaFire} boxSize={4} />
            <span>Monkey Roast</span>
          </HStack>
        </Badge>
        <Badge
          colorScheme={roast.score >= 75 ? "green" : roast.score >= 50 ? "orange" : "red"}
          fontSize="1em"
          borderRadius="md"
          px={2}
        >
          {roast.score}/100
        </Badge>
        {roast.updatedAt && (
          <Text fontSize="xs" color="gray.500">
            {new Date(roast.updatedAt).toLocaleDateString()}
          </Text>
        )}
      </Flex>

      {roast.imageAnalysis && (
        <Box bg={bg} p={5} borderRadius="xl" border="1px solid" borderColor={borderColor} mb={6} boxShadow="lg">
          <HStack mb={3}>
            <Icon as={FiImage} boxSize={5} color="orange.400" />
            <Text fontWeight="bold">{t("monkeyRoastImageAnalysis")}</Text>
          </HStack>
          <Text fontSize="sm" color="gray.300">
            {roast.imageAnalysis}
          </Text>
        </Box>
      )}

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
        {roast.whatImagesDo && roast.whatImagesDo.length > 0 && (
          <Box bg={bg} p={5} borderRadius="xl" border="1px solid" borderColor={borderColor} boxShadow="lg">
            <HStack mb={3} color="green.400">
              <Icon as={FiTrendingUp} boxSize={5} />
              <Text fontWeight="bold">{t("monkeyRoastWhatImagesDo")}</Text>
            </HStack>
            <List spacing={2}>
              {roast.whatImagesDo.map((item, i) => (
                <ListItem key={i} display="flex" alignItems="center" fontSize="sm">
                  <ListIcon as={FiTrendingUp} color="green.400" />
                  {item}
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {roast.potentialProblems && roast.potentialProblems.length > 0 && (
          <Box bg={bg} p={5} borderRadius="xl" border="1px solid" borderColor={borderColor} boxShadow="lg">
            <HStack mb={3} color="orange.400">
              <Icon as={FiAlertTriangle} boxSize={5} />
              <Text fontWeight="bold">{t("monkeyRoastPotentialProblems")}</Text>
            </HStack>
            <List spacing={2}>
              {roast.potentialProblems.map((item, i) => (
                <ListItem key={i} display="flex" alignItems="center" fontSize="sm" color="orange.300">
                  <ListIcon as={FiAlertTriangle} color="orange.400" />
                  {item}
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {roast.whatImagesDontUnlock && roast.whatImagesDontUnlock.length > 0 && (
          <Box bg={bg} p={5} borderRadius="xl" border="1px solid" borderColor={borderColor} boxShadow="lg" gridColumn={{ md: "1 / -1" }}>
            <HStack mb={3} color="red.300">
              <Icon as={FiEyeOff} boxSize={5} />
              <Text fontWeight="bold">{t("monkeyRoastWhatImagesDontUnlock")}</Text>
            </HStack>
            <List spacing={2}>
              {roast.whatImagesDontUnlock.map((item, i) => (
                <ListItem key={i} display="flex" alignItems="center" fontSize="sm" color="red.300">
                  <ListIcon as={FiEyeOff} color="red.400" />
                  {item}
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </SimpleGrid>
    </Box>
  );
};

export default MonkeyRoastReport;
